import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { getFirebaseDb } from './firebase';
import type {
  BeliefNode,
  BeliefEdge,
  CognitiveMotif,
  GraphGapAnalysis,
  GraphStatistics,
  NodeDomain,
  NodeType,
  EdgeRelation,
  EdgePolarity,
  ExtractionResult,
  BeliefTier,
} from '../types';

// ─── Path Helpers ────────────────────────────────────────────────────────────

const paths = {
  nodes: (userId: string) => collection(getFirebaseDb(), 'users', userId, 'nodes'),
  node: (userId: string, nodeId: string) => doc(getFirebaseDb(), 'users', userId, 'nodes', nodeId),
  edges: (userId: string) => collection(getFirebaseDb(), 'users', userId, 'edges'),
  edge: (userId: string, edgeId: string) => doc(getFirebaseDb(), 'users', userId, 'edges', edgeId),
  motifs: (userId: string) => collection(getFirebaseDb(), 'users', userId, 'motifs'),
  motif: (userId: string, motifId: string) => doc(getFirebaseDb(), 'users', userId, 'motifs', motifId),
  graphMeta: (userId: string, docId: 'coverage' | 'stats') =>
    doc(getFirebaseDb(), 'users', userId, 'graphMeta', docId),
};

// ─── Nodes ───────────────────────────────────────────────────────────────────

export async function getNode(userId: string, nodeId: string): Promise<BeliefNode | null> {
  const snap = await getDoc(paths.node(userId, nodeId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as BeliefNode;
}

export async function getAllNodes(userId: string): Promise<BeliefNode[]> {
  const snap = await getDocs(paths.nodes(userId));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as BeliefNode));
}

export async function getNodesByDomain(userId: string, domain: NodeDomain): Promise<BeliefNode[]> {
  const q = query(paths.nodes(userId), where('domain', 'array-contains', domain));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as BeliefNode));
}

export async function createNode(
  userId: string,
  data: Omit<BeliefNode, 'id' | 'createdAt' | 'updatedAt' | 'lastReinforced'>
): Promise<string> {
  const now = Timestamp.now();
  const ref = await addDoc(paths.nodes(userId), {
    ...data,
    createdAt: now,
    updatedAt: now,
    lastReinforced: now,
  });
  return ref.id;
}

export async function updateNode(
  userId: string,
  nodeId: string,
  updates: Partial<Omit<BeliefNode, 'id' | 'createdAt'>>
): Promise<void> {
  await updateDoc(paths.node(userId, nodeId), {
    ...updates,
    updatedAt: Timestamp.now(),
  });
}

export async function deleteNode(userId: string, nodeId: string): Promise<void> {
  await deleteDoc(paths.node(userId, nodeId));
}

// ─── Edges ───────────────────────────────────────────────────────────────────

export async function getEdge(userId: string, edgeId: string): Promise<BeliefEdge | null> {
  const snap = await getDoc(paths.edge(userId, edgeId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as BeliefEdge;
}

export async function getAllEdges(userId: string): Promise<BeliefEdge[]> {
  const snap = await getDocs(paths.edges(userId));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as BeliefEdge));
}

export async function getEdgesForNode(userId: string, nodeId: string): Promise<BeliefEdge[]> {
  const [sourceSnap, targetSnap] = await Promise.all([
    getDocs(query(paths.edges(userId), where('sourceNodeId', '==', nodeId))),
    getDocs(query(paths.edges(userId), where('targetNodeId', '==', nodeId))),
  ]);
  const sourceEdges = sourceSnap.docs.map(d => ({ id: d.id, ...d.data() } as BeliefEdge));
  const targetEdges = targetSnap.docs.map(d => ({ id: d.id, ...d.data() } as BeliefEdge));
  return [...sourceEdges, ...targetEdges];
}

export async function createEdge(
  userId: string,
  data: Omit<BeliefEdge, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  const now = Timestamp.now();
  const ref = await addDoc(paths.edges(userId), { ...data, createdAt: now, updatedAt: now });
  return ref.id;
}

export async function updateEdge(
  userId: string,
  edgeId: string,
  updates: Partial<Omit<BeliefEdge, 'id' | 'createdAt'>>
): Promise<void> {
  await updateDoc(paths.edge(userId, edgeId), { ...updates, updatedAt: Timestamp.now() });
}

export async function deleteEdge(userId: string, edgeId: string): Promise<void> {
  await deleteDoc(paths.edge(userId, edgeId));
}

// ─── Motifs ──────────────────────────────────────────────────────────────────

export async function getAllMotifs(userId: string): Promise<CognitiveMotif[]> {
  const snap = await getDocs(paths.motifs(userId));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as CognitiveMotif));
}

export async function createMotif(
  userId: string,
  data: Omit<CognitiveMotif, 'id' | 'firstSeen' | 'lastSeen'>
): Promise<string> {
  const now = Timestamp.now();
  const ref = await addDoc(paths.motifs(userId), { ...data, firstSeen: now, lastSeen: now });
  return ref.id;
}

export async function updateMotif(
  userId: string,
  motifId: string,
  updates: Partial<Omit<CognitiveMotif, 'id' | 'firstSeen'>>
): Promise<void> {
  await updateDoc(paths.motif(userId, motifId), {
    ...updates,
    lastSeen: Timestamp.now(),
  });
}

export async function deleteMotif(userId: string, motifId: string): Promise<void> {
  await deleteDoc(paths.motif(userId, motifId));
}

// ─── Graph Meta ──────────────────────────────────────────────────────────────

export async function getGraphGapAnalysis(userId: string): Promise<GraphGapAnalysis | null> {
  const snap = await getDoc(paths.graphMeta(userId, 'coverage'));
  if (!snap.exists()) return null;
  return snap.data() as GraphGapAnalysis;
}

export async function saveGraphGapAnalysis(userId: string, analysis: GraphGapAnalysis): Promise<void> {
  await setDoc(paths.graphMeta(userId, 'coverage'), analysis);
}

export async function getGraphStatistics(userId: string): Promise<GraphStatistics | null> {
  const snap = await getDoc(paths.graphMeta(userId, 'stats'));
  if (!snap.exists()) return null;
  return snap.data() as GraphStatistics;
}

export async function saveGraphStatistics(userId: string, stats: GraphStatistics): Promise<void> {
  await setDoc(paths.graphMeta(userId, 'stats'), stats);
}

// ─── Batch Graph Update ──────────────────────────────────────────────────────
// Applies an ExtractionResult to the graph. Returns the real node IDs
// created so callers can build the session extraction record.

export interface BatchUpdateResult {
  createdNodeIds: string[];
  createdEdgeIds: string[];
  reinforcedNodeIds: string[];
  revisedEdgeIds: string[];
}

export async function batchUpdateGraph(
  userId: string,
  result: ExtractionResult,
  sessionId: string
): Promise<BatchUpdateResult> {
  const db = getFirebaseDb();
  const batch = writeBatch(db);
  const now = Timestamp.now();

  const createdNodeIds: string[] = [];
  const createdEdgeIds: string[] = [];
  const reinforcedNodeIds: string[] = [];
  const revisedEdgeIds: string[] = [];

  // Map suggestedId → real Firestore ID for cross-referencing edges
  const nodeIdMap = new Map<string, string>();

  // 1. Create new nodes
  for (const n of result.newNodes) {
    const newRef = doc(paths.nodes(userId));
    nodeIdMap.set(n.suggestedId, newRef.id);
    createdNodeIds.push(newRef.id);

    batch.set(newRef, {
      label: n.label,
      domain: [n.domain as NodeDomain],
      type: n.type as NodeType,
      tier: (n.tier as BeliefTier) || 'active',
      tierHistory: [],
      confidence: n.confidence,
      valence: n.valence,
      source: sessionId,
      lastReinforced: now,
      extractedQuote: n.extractedQuote,
      reinforcements: [{
        sessionId,
        quote: n.extractedQuote,
        timestamp: now,
        context: n.reasoning,
      }],
      parentNodeId: undefined,
      childNodeIds: [],
      isConsolidated: false,
      status: 'active' as const,
      createdAt: now,
      updatedAt: now,
    } satisfies Omit<BeliefNode, 'id'>);
  }

  // 2. Reinforce existing nodes
  for (const r of result.reinforcedNodes) {
    const nodeRef = paths.node(userId, r.nodeId);
    batch.update(nodeRef, {
      confidence: r.newConfidence,
      lastReinforced: now,
      updatedAt: now,
    });
    reinforcedNodeIds.push(r.nodeId);
  }

  // 3. Create new edges (resolve suggestedIds to real IDs)
  for (const e of result.newEdges) {
    const sourceId = nodeIdMap.get(e.sourceNodeId) ?? e.sourceNodeId;
    const targetId = nodeIdMap.get(e.targetNodeId) ?? e.targetNodeId;
    const newRef = doc(paths.edges(userId));
    createdEdgeIds.push(newRef.id);

    batch.set(newRef, {
      sourceNodeId: sourceId,
      targetNodeId: targetId,
      relation: e.relation as EdgeRelation,
      strength: e.strength,
      polarity: e.polarity as EdgePolarity,
      evidence: [sessionId],
      createdAt: now,
      updatedAt: now,
    } satisfies Omit<BeliefEdge, 'id'>);
  }

  // 4. Revise existing edges
  for (const rev of result.revisedEdges) {
    const edgeRef = paths.edge(userId, rev.edgeId);
    const edgeUpdates: Record<string, unknown> = { updatedAt: now };
    if (rev.newStrength !== undefined) edgeUpdates.strength = rev.newStrength;
    if (rev.newPolarity !== undefined) edgeUpdates.polarity = rev.newPolarity;
    batch.update(edgeRef, edgeUpdates);
    revisedEdgeIds.push(rev.edgeId);
  }

  await batch.commit();

  return { createdNodeIds, createdEdgeIds, reinforcedNodeIds, revisedEdgeIds };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Returns a snapshot of the full graph for use in system prompts / gap analysis. */
export async function getFullGraph(userId: string) {
  const [nodes, edges, motifs] = await Promise.all([
    getAllNodes(userId),
    getAllEdges(userId),
    getAllMotifs(userId),
  ]);
  return { nodes, edges, motifs };
}

export interface PruneNodeResult {
  removedEdgeCount: number;
  removedMotifCount: number;
}

/**
 * Removes a node and cleans up graph artifacts that depend on it.
 * This is intended for user-driven corrections on incorrectly extracted concepts.
 */
export async function pruneNode(userId: string, nodeId: string): Promise<PruneNodeResult> {
  const db = getFirebaseDb();
  const [connectedEdges, motifs] = await Promise.all([
    getEdgesForNode(userId, nodeId),
    getAllMotifs(userId),
  ]);
  const motifsToDelete = motifs.filter(motif => motif.chain.includes(nodeId));

  const batch = writeBatch(db);
  batch.delete(paths.node(userId, nodeId));
  for (const edge of connectedEdges) {
    batch.delete(paths.edge(userId, edge.id));
  }
  for (const motif of motifsToDelete) {
    batch.delete(paths.motif(userId, motif.id));
  }
  await batch.commit();

  return {
    removedEdgeCount: connectedEdges.length,
    removedMotifCount: motifsToDelete.length,
  };
}
